import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  FileText, 
  Save, 
  ArrowLeft,
  Image as ImageIcon,
  Tag,
  Calendar,
  Eye,
  Copy,
  Check,
  Bold,
  Italic,
  Underline,
  List,
  ListOrdered,
  Link as LinkIcon,
  Heading1,
  Heading2,
  Code,
  Type
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { fetchPostById, createPost, updatePost, fetchCategories } from '../services/newsApi';
import CustomSelect from '../components/common/CustomSelect';

const AdminPostEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const isEditMode = !!id;

  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    featured_image: '',
    category_id: '',
    status: 'draft',
    published_at: ''
  });

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(isEditMode);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [uploading, setUploading] = useState({});
  const [contentImages, setContentImages] = useState({
    image1: '',
    image2: '',
    image3: ''
  });
  const [copiedUrl, setCopiedUrl] = useState(null);
  const [editorMode, setEditorMode] = useState('visual'); // 'visual' or 'code'
  const visualEditorRef = useRef(null);
  const codeEditorRef = useRef(null);
  const isInitialMount = useRef(true);

  useEffect(() => {
    loadCategories();
    if (isEditMode) {
      loadPost();
    }
  }, [id]);

  // Set initial content in visual editor
  useEffect(() => {
    if (visualEditorRef.current && formData.content && isInitialMount.current) {
      visualEditorRef.current.innerHTML = formData.content;
      isInitialMount.current = false;
    }
  }, [formData.content]);

  const loadCategories = async () => {
    try {
      const data = await fetchCategories();
      setCategories(data);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const loadPost = async () => {
    try {
      setLoading(true);
      const post = await fetchPostById(parseInt(id));
      setFormData({
        title: post.title || '',
        slug: post.slug || '',
        excerpt: post.excerpt || '',
        content: post.content || '',
        featured_image: post.featured_image || '',
        category_id: post.category_id || '',
        status: post.status || 'draft',
        published_at: post.published_at ? post.published_at.split(' ')[0] : ''
      });
    } catch (error) {
      console.error('Error loading post:', error);
      alert('Failed to load post');
      navigate('/admin/news');
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const updated = { ...prev, [name]: value };
      if (name === 'title' && !isEditMode) {
        updated.slug = generateSlug(value);
      }
      return updated;
    });
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  // Handle visual editor changes
  const handleVisualEditorChange = () => {
    if (visualEditorRef.current) {
      const htmlContent = visualEditorRef.current.innerHTML;
      setFormData(prev => ({ ...prev, content: htmlContent }));
    }
  };

  // Switch between visual and code mode
  const switchEditorMode = (mode) => {
    if (mode === 'code' && editorMode === 'visual') {
      if (visualEditorRef.current) {
        setFormData(prev => ({ ...prev, content: visualEditorRef.current.innerHTML }));
      }
    } else if (mode === 'visual' && editorMode === 'code') {
      setTimeout(() => {
        if (visualEditorRef.current) {
          visualEditorRef.current.innerHTML = formData.content;
        }
      }, 0);
    }
    setEditorMode(mode);
  };

  // Execute formatting command
  const executeCommand = (command, value = null) => {
    document.execCommand(command, false, value);
    visualEditorRef.current?.focus();
    handleVisualEditorChange();
  };

  const formatBold = () => executeCommand('bold');
  const formatItalic = () => executeCommand('italic');
  const formatUnderline = () => executeCommand('underline');
  const formatH1 = () => executeCommand('formatBlock', '<h1>');
  const formatH2 = () => executeCommand('formatBlock', '<h2>');
  const formatParagraph = () => executeCommand('formatBlock', '<p>');
  const formatUL = () => executeCommand('insertUnorderedList');
  const formatOL = () => executeCommand('insertOrderedList');

  const formatLink = () => {
    const url = prompt('Enter URL:');
    if (url) executeCommand('createLink', url);
  };

  const formatImage = () => {
    const url = prompt('Enter image URL:');
    if (url) executeCommand('insertImage', url);
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.title.trim()) newErrors.title = 'Title is required';
    if (!formData.slug.trim()) newErrors.slug = 'Slug is required';
    if (!formData.content.trim()) newErrors.content = 'Content is required';
    if (!formData.category_id) newErrors.category_id = 'Category is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const compressImage = async (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;

          const maxWidth = 1200;
          const maxHeight = 1200;

          if (width > height) {
            if (width > maxWidth) {
              height *= maxWidth / width;
              width = maxWidth;
            }
          } else {
            if (height > maxHeight) {
              width *= maxHeight / height;
              height = maxHeight;
            }
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.85);
        };
      };
    });
  };

  // ===== NEW: upload an image FILE and return URL =====
  const uploadImageFile = async (file) => {
    const compressedBlob = await compressImage(file);
    const compressedFile = new File([compressedBlob], file.name, { type: 'image/jpeg' });

    const fd = new FormData();
    fd.append('image', compressedFile);

    const response = await fetch('https://api.fsgu.co.za/upload-image.php', {
      method: 'POST',
      body: fd
    });

    const data = await response.json();
    if (!data.success) throw new Error(data.message || 'Upload failed');
    if (!data.url) throw new Error('Upload succeeded but no url returned');
    return data.url;
  };

  // ===== NEW: insert image URL at current cursor position in visual editor =====
  const insertImageAtCursor = (url) => {
    if (!visualEditorRef.current) return;

    visualEditorRef.current.focus();

    // execCommand works with your existing approach and preserves cursor insertion
    document.execCommand('insertImage', false, url);

    // Optional: make sure images are block-level (prevents weird inline spacing)
    // We wrap the last inserted img if needed:
    const imgs = visualEditorRef.current.querySelectorAll('img');
    const lastImg = imgs[imgs.length - 1];
    if (lastImg) {
      lastImg.style.display = 'block';
      lastImg.style.maxWidth = '100%';
      lastImg.style.height = 'auto';
      lastImg.style.margin = '1.5rem auto';
      lastImg.style.borderRadius = '0.75rem';
    }

    handleVisualEditorChange();
  };

  // ===== NEW: handle drop into editor =====
  const handleEditorDrop = async (e) => {
    // Only in visual mode
    if (editorMode !== 'visual') return;

    const dt = e.dataTransfer;
    if (!dt?.files?.length) return;

    const imageFiles = Array.from(dt.files).filter(f => f.type.startsWith('image/'));
    if (imageFiles.length === 0) return;

    e.preventDefault(); // stop browser inserting base64/local path

    try {
      setUploading(prev => ({ ...prev, editorDrop: true }));
      for (const file of imageFiles) {
        const url = await uploadImageFile(file);
        insertImageAtCursor(url);
      }
    } catch (err) {
      console.error('Drop upload failed:', err);
      alert('Failed to upload dropped image: ' + (err?.message || 'Unknown error'));
    } finally {
      setUploading(prev => ({ ...prev, editorDrop: false }));
    }
  };

  // ===== NEW: handle paste into editor (Ctrl+V images) =====
  const handleEditorPaste = async (e) => {
    if (editorMode !== 'visual') return;

    const items = e.clipboardData?.items;
    if (!items?.length) return;

    const imageItems = Array.from(items).filter(i => i.type?.startsWith('image/'));
    if (imageItems.length === 0) return;

    e.preventDefault(); // stop browser inserting base64

    try {
      setUploading(prev => ({ ...prev, editorPaste: true }));
      for (const item of imageItems) {
        const file = item.getAsFile();
        if (!file) continue;
        const url = await uploadImageFile(file);
        insertImageAtCursor(url);
      }
    } catch (err) {
      console.error('Paste upload failed:', err);
      alert('Failed to upload pasted image: ' + (err?.message || 'Unknown error'));
    } finally {
      setUploading(prev => ({ ...prev, editorPaste: false }));
    }
  };

  const handleImageUpload = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setUploading(prev => ({ ...prev, [type]: true }));

      const url = await uploadImageFile(file);

      if (type === 'featured') {
        setFormData(prev => ({ ...prev, featured_image: url }));
      } else {
        const imageNum = type.replace('content', '');
        setContentImages(prev => ({ ...prev, [`image${imageNum}`]: url }));
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Failed to upload image: ' + error.message);
    } finally {
      setUploading(prev => ({ ...prev, [type]: false }));
    }
  };

  const copyToClipboard = (text, imageNum) => {
    navigator.clipboard.writeText(text);
    setCopiedUrl(imageNum);
    setTimeout(() => setCopiedUrl(null), 2000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // IMPORTANT: capture visual editor HTML into a local variable (avoid setState timing)
    let contentToSave = formData.content;
    if (editorMode === 'visual' && visualEditorRef.current) {
      contentToSave = visualEditorRef.current.innerHTML;
    }

    // validate using the captured content
    const newErrors = {};
    if (!formData.title.trim()) newErrors.title = 'Title is required';
    if (!formData.slug.trim()) newErrors.slug = 'Slug is required';
    if (!contentToSave.trim()) newErrors.content = 'Content is required';
    if (!formData.category_id) newErrors.category_id = 'Category is required';
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    try {
      setSubmitting(true);

      const postData = {
        ...formData,
        content: contentToSave,
        category_id: parseInt(formData.category_id),
        published_at: formData.status === 'published' && formData.published_at 
          ? formData.published_at 
          : (formData.status === 'published' ? new Date().toISOString().split('T')[0] : null)
      };

      if (isEditMode) {
        await updatePost(parseInt(id), postData);
        alert('Post updated successfully!');
      } else {
        await createPost(postData);
        alert('Post created successfully!');
      }

      navigate('/admin/news');
    } catch (error) {
      console.error('Error saving post:', error);
      alert(error.message || `Failed to ${isEditMode ? 'update' : 'create'} post`);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${
        isDarkMode ? 'bg-fsgu-gray-900' : 'bg-fsgu-silver-50'
      }`}>
        <div className="text-center">
          <div className={`inline-block animate-spin rounded-full h-12 w-12 border-4 border-solid border-current border-r-transparent mb-4 ${
            isDarkMode ? 'text-fsgu-green-400' : 'text-fsgu-green-500'
          }`}></div>
          <p className={isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'}>
            Loading post...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${
      isDarkMode ? 'bg-fsgu-gray-900' : 'bg-fsgu-silver-50'
    }`}>
      {/* Header */}
      <div className={`sticky top-0 z-30 border-b ${
        isDarkMode
          ? 'bg-fsgu-gray-900/95 border-fsgu-gray-700 backdrop-blur-sm'
          : 'bg-fsgu-silver-50/95 border-fsgu-silver-200 backdrop-blur-sm'
      }`}>
        <div className="container-custom py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            {/* Left: Back + Title */}
            <div className="flex items-center gap-3">
              <Link
                to="/admin/news"
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode
                    ? 'hover:bg-fsgu-gray-800 text-fsgu-silver-300'
                    : 'hover:bg-fsgu-silver-100 text-fsgu-silver-600'
                }`}
              >
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div>
                <h1 className={`text-3xl sm:text-4xl font-bold font-cursive ${
                  isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
                }`}>
                  {isEditMode ? 'Edit Post' : 'New Post'}
                </h1>
                <p className={`text-sm ${
                  isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
                }`}>
                  {isEditMode ? 'Update post content' : 'Write a new article'}
                </p>
              </div>
            </div>

            {/* Right: Action Buttons */}
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => navigate('/admin/news')}
                className="btn btn-outline"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="btn btn-primary inline-flex items-center"
              >
                {submitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    {isEditMode ? 'Update' : 'Create'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Form Content */}
      <div className="container-custom py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content - 2/3 width */}
          <div className="lg:col-span-2 space-y-6">
            {/* Title */}
            <div className={`p-6 rounded-xl border ${
              isDarkMode 
                ? 'bg-fsgu-gray-800 border-fsgu-gray-700' 
                : 'bg-fsgu-white border-fsgu-silver-200 shadow-sm'
            }`}>
              <label className={`block text-sm font-medium mb-2 ${
                isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
              }`}>
                Title <span className="text-fsgu-red-500">*</span>
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                className={`w-full px-4 py-3 text-2xl font-bold rounded-lg border transition-colors ${
                  errors.title 
                    ? 'border-red-500' 
                    : isDarkMode
                    ? 'bg-fsgu-gray-700 border-fsgu-gray-600 text-fsgu-silver-100 placeholder-fsgu-silver-500'
                    : 'bg-fsgu-white border-fsgu-silver-300 text-fsgu-silver-900 placeholder-fsgu-silver-400'
                }`}
                placeholder="Enter post title..."
              />
              {errors.title && (
                <p className="text-red-500 text-sm mt-1">{errors.title}</p>
              )}
            </div>

            {/* Slug */}
            <div className={`p-6 rounded-xl border ${
              isDarkMode 
                ? 'bg-fsgu-gray-800 border-fsgu-gray-700' 
                : 'bg-fsgu-white border-fsgu-silver-200 shadow-sm'
            }`}>
              <label className={`block text-sm font-medium mb-2 ${
                isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
              }`}>
                URL Slug <span className="text-fsgu-red-500">*</span>
              </label>
              <input
                type="text"
                name="slug"
                value={formData.slug}
                onChange={handleChange}
                className={`input ${errors.slug ? 'border-red-500' : ''}`}
                placeholder="post-url-slug"
              />
              {errors.slug && (
                <p className="text-red-500 text-sm mt-1">{errors.slug}</p>
              )}
              <p className={`text-xs mt-2 ${
                isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-600'
              }`}>
                URL: /news/{formData.slug || 'post-url-slug'}
              </p>
            </div>

            {/* Excerpt */}
            <div className={`p-6 rounded-xl border ${
              isDarkMode 
                ? 'bg-fsgu-gray-800 border-fsgu-gray-700' 
                : 'bg-fsgu-white border-fsgu-silver-200 shadow-sm'
            }`}>
              <label className={`block text-sm font-medium mb-2 ${
                isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
              }`}>
                Excerpt
              </label>
              <textarea
                name="excerpt"
                value={formData.excerpt}
                onChange={handleChange}
                rows={3}
                className="input"
                placeholder="Brief summary (shown in previews)..."
              />
            </div>

            {/* Content Editor */}
            <div className={`p-6 rounded-xl border ${
              isDarkMode 
                ? 'bg-fsgu-gray-800 border-fsgu-gray-700' 
                : 'bg-fsgu-white border-fsgu-silver-200 shadow-sm'
            }`}>
              <div className="flex items-center justify-between mb-4">
                <label className={`block text-sm font-medium ${
                  isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                }`}>
                  Content <span className="text-fsgu-red-500">*</span>
                </label>

                {/* Mode Toggle */}
                <div className={`inline-flex rounded-lg p-1 ${
                  isDarkMode ? 'bg-fsgu-gray-700' : 'bg-fsgu-silver-100'
                }`}>
                  <button
                    type="button"
                    onClick={() => switchEditorMode('visual')}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors inline-flex items-center ${
                      editorMode === 'visual'
                        ? 'bg-fsgu-green-500 text-white'
                        : isDarkMode
                        ? 'text-fsgu-silver-400 hover:text-fsgu-silver-200'
                        : 'text-fsgu-silver-600 hover:text-fsgu-silver-900'
                    }`}
                  >
                    <Type className="w-3.5 h-3.5 mr-1.5" />
                    Visual
                  </button>
                  <button
                    type="button"
                    onClick={() => switchEditorMode('code')}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors inline-flex items-center ${
                      editorMode === 'code'
                        ? 'bg-fsgu-green-500 text-white'
                        : isDarkMode
                        ? 'text-fsgu-silver-400 hover:text-fsgu-silver-200'
                        : 'text-fsgu-silver-600 hover:text-fsgu-silver-900'
                    }`}
                  >
                    <Code className="w-3.5 h-3.5 mr-1.5" />
                    Code
                  </button>
                </div>
              </div>

              {/* Visual Editor Mode */}
              {editorMode === 'visual' && (
                <>
                  {/* Formatting Toolbar */}
                  <div className={`mb-3 p-2 rounded-lg border flex flex-wrap gap-1 ${
                    isDarkMode 
                      ? 'bg-fsgu-gray-700 border-fsgu-gray-600' 
                      : 'bg-fsgu-silver-50 border-fsgu-silver-300'
                  }`}>
                    <button type="button" onClick={formatBold}
                      className={`p-2 rounded hover:bg-fsgu-green-500/20 transition-colors ${isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'}`}
                      title="Bold (Ctrl+B)"
                    ><Bold className="w-4 h-4" /></button>

                    <button type="button" onClick={formatItalic}
                      className={`p-2 rounded hover:bg-fsgu-green-500/20 transition-colors ${isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'}`}
                      title="Italic (Ctrl+I)"
                    ><Italic className="w-4 h-4" /></button>

                    <button type="button" onClick={formatUnderline}
                      className={`p-2 rounded hover:bg-fsgu-green-500/20 transition-colors ${isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'}`}
                      title="Underline (Ctrl+U)"
                    ><Underline className="w-4 h-4" /></button>

                    <div className={`w-px h-8 mx-1 ${isDarkMode ? 'bg-fsgu-gray-600' : 'bg-fsgu-silver-300'}`}></div>

                    <button type="button" onClick={formatH1}
                      className={`p-2 rounded hover:bg-fsgu-green-500/20 transition-colors ${isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'}`}
                      title="Heading 1"
                    ><Heading1 className="w-4 h-4" /></button>

                    <button type="button" onClick={formatH2}
                      className={`p-2 rounded hover:bg-fsgu-green-500/20 transition-colors ${isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'}`}
                      title="Heading 2"
                    ><Heading2 className="w-4 h-4" /></button>

                    <div className={`w-px h-8 mx-1 ${isDarkMode ? 'bg-fsgu-gray-600' : 'bg-fsgu-silver-300'}`}></div>

                    <button type="button" onClick={formatUL}
                      className={`p-2 rounded hover:bg-fsgu-green-500/20 transition-colors ${isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'}`}
                      title="Bullet List"
                    ><List className="w-4 h-4" /></button>

                    <button type="button" onClick={formatOL}
                      className={`p-2 rounded hover:bg-fsgu-green-500/20 transition-colors ${isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'}`}
                      title="Numbered List"
                    ><ListOrdered className="w-4 h-4" /></button>

                    <div className={`w-px h-8 mx-1 ${isDarkMode ? 'bg-fsgu-gray-600' : 'bg-fsgu-silver-300'}`}></div>

                    <button type="button" onClick={formatLink}
                      className={`p-2 rounded hover:bg-fsgu-green-500/20 transition-colors ${isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'}`}
                      title="Insert Link"
                    ><LinkIcon className="w-4 h-4" /></button>

                    <button type="button" onClick={formatImage}
                      className={`p-2 rounded hover:bg-fsgu-green-500/20 transition-colors ${isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'}`}
                      title="Insert Image"
                    ><ImageIcon className="w-4 h-4" /></button>

                    {(uploading.editorDrop || uploading.editorPaste) && (
                      <span className={`ml-auto text-sm ${isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'}`}>
                        Uploading image...
                      </span>
                    )}
                  </div>

                  {/* Visual Editor */}
                  <div
                    ref={visualEditorRef}
                    contentEditable
                    onInput={handleVisualEditorChange}
                    onDrop={handleEditorDrop}
                    onPaste={handleEditorPaste}
                    className={`min-h-[500px] p-4 rounded-lg border transition-colors prose max-w-none ${
                      errors.content 
                        ? 'border-red-500' 
                        : isDarkMode
                        ? 'bg-fsgu-gray-700 border-fsgu-gray-600 text-fsgu-silver-100'
                        : 'bg-fsgu-white border-fsgu-silver-300 text-fsgu-silver-900'
                    }`}
                    style={{
                      outline: 'none',
                      overflowY: 'auto'
                    }}
                  />
                </>
              )}

              {/* Code Editor Mode */}
              {editorMode === 'code' && (
                <textarea
                  ref={codeEditorRef}
                  name="content"
                  value={formData.content}
                  onChange={handleChange}
                  rows={20}
                  className={`input font-mono text-sm ${errors.content ? 'border-red-500' : ''}`}
                  placeholder="Write your post content here... (HTML supported)"
                />
              )}

              {errors.content && (
                <p className="text-red-500 text-sm mt-2">{errors.content}</p>
              )}
              <p className={`text-xs mt-2 ${
                isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-600'
              }`}>
                {editorMode === 'visual' 
                  ? 'Use the toolbar to format your content. You can also drag & drop or paste images directly into the editor.'
                  : 'Write HTML directly. Switch to Visual view to use the formatting toolbar.'}
              </p>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Publish Settings */}
            <div className={`p-6 rounded-xl border ${
              isDarkMode 
                ? 'bg-fsgu-gray-800 border-fsgu-gray-700' 
                : 'bg-fsgu-white border-fsgu-silver-200 shadow-sm'
            }`}>
              <h3 className={`text-lg font-semibold mb-4 flex items-center ${
                isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
              }`}>
                <Eye className={`w-5 h-5 mr-2 ${
                  isDarkMode ? 'text-fsgu-green-400' : 'text-fsgu-green-500'
                }`} />
                Publish Settings
              </h3>

              <div className="mb-4">
                <label className={`block text-sm font-medium mb-2 ${
                  isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                }`}>
                  Status
                </label>
                <CustomSelect
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  options={[
                    { value: 'draft', label: 'Draft' },
                    { value: 'published', label: 'Published' },
                    { value: 'archived', label: 'Archived' }
                  ]}
                  placeholder="Select status"
                />
              </div>

              {formData.status === 'published' && (
                <div>
                  <label className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                  }`}>
                    Publish Date
                  </label>
                  <input
                    type="date"
                    name="published_at"
                    value={formData.published_at}
                    onChange={handleChange}
                    className="input"
                  />
                </div>
              )}
            </div>

            {/* Category */}
            <div className={`p-6 rounded-xl border ${
              isDarkMode 
                ? 'bg-fsgu-gray-800 border-fsgu-gray-700' 
                : 'bg-fsgu-white border-fsgu-silver-200 shadow-sm'
            }`}>
              <h3 className={`text-lg font-semibold mb-4 flex items-center ${
                isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
              }`}>
                <Tag className={`w-5 h-5 mr-2 ${
                  isDarkMode ? 'text-fsgu-green-400' : 'text-fsgu-green-500'
                }`} />
                Category
              </h3>

              <CustomSelect
                name="category_id"
                value={formData.category_id}
                onChange={handleChange}
                options={[
                  { value: '', label: 'Select a category' },
                  ...categories.map(cat => ({
                    value: cat.id.toString(),
                    label: cat.name
                  }))
                ]}
                placeholder="Select a category"
              />
              {errors.category_id && (
                <p className="text-red-500 text-sm mt-1">{errors.category_id}</p>
              )}
            </div>

            {/* Featured Image */}
            <div className={`p-6 rounded-xl border ${
              isDarkMode 
                ? 'bg-fsgu-gray-800 border-fsgu-gray-700' 
                : 'bg-fsgu-white border-fsgu-silver-200 shadow-sm'
            }`}>
              <h3 className={`text-lg font-semibold mb-4 flex items-center ${
                isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
              }`}>
                <ImageIcon className={`w-5 h-5 mr-2 ${
                  isDarkMode ? 'text-fsgu-green-400' : 'text-fsgu-green-500'
                }`} />
                Featured Image
              </h3>

              <p className={`text-sm mb-3 ${
                isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
              }`}>
                Upload an image or paste URL
              </p>

              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleImageUpload(e, 'featured')}
                className="input mb-2"
              />

              <input
                type="url"
                name="featured_image"
                value={formData.featured_image}
                onChange={handleChange}
                className="input"
                placeholder="Or paste image URL"
              />

              {uploading.featured && (
                <div className="mt-2 text-sm text-fsgu-green-500">Uploading...</div>
              )}

              {formData.featured_image && (
                <div className="mt-3">
                  <img 
                    src={formData.featured_image} 
                    alt="Preview" 
                    className="w-full h-32 object-cover rounded-lg"
                    onError={(e) => e.target.style.display = 'none'}
                  />
                </div>
              )}
            </div>

            {/* Content Images (kept exactly as you had it) */}
            <div className={`p-6 rounded-xl border ${
              isDarkMode 
                ? 'bg-fsgu-gray-800 border-fsgu-gray-700' 
                : 'bg-fsgu-white border-fsgu-silver-200 shadow-sm'
            }`}>
              <h3 className={`text-lg font-semibold mb-4 flex items-center ${
                isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
              }`}>
                <ImageIcon className={`w-5 h-5 mr-2 ${
                  isDarkMode ? 'text-fsgu-green-400' : 'text-fsgu-green-500'
                }`} />
                Content Images
              </h3>

              <p className={`text-sm mb-3 ${
                isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
              }`}>
                Upload up to 3 images for your post content
              </p>

              {[1, 2, 3].map(num => (
                <div key={num} className={`mb-4 pb-4 last:border-0 ${
                  num !== 3 ? `border-b ${isDarkMode ? 'border-fsgu-gray-700' : 'border-fsgu-silver-200'}` : ''
                }`}>
                  <label className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                  }`}>
                    Image {num}
                  </label>

                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload(e, `content${num}`)}
                    className="input mb-2"
                  />

                  {uploading[`content${num}`] && (
                    <div className="text-sm text-fsgu-green-500 mb-2">Uploading...</div>
                  )}

                  {contentImages[`image${num}`] && (
                    <div className="mt-2">
                      <img 
                        src={contentImages[`image${num}`]} 
                        alt={`Content ${num}`}
                        className="w-full h-24 object-cover rounded-lg mb-2"
                      />
                      <div className="flex gap-2">
                        <input
                          type="text"
                          readOnly
                          value={contentImages[`image${num}`]}
                          className={`flex-1 px-3 py-1.5 text-xs rounded border ${
                            isDarkMode
                              ? 'bg-fsgu-gray-700 border-fsgu-gray-600 text-fsgu-silver-300'
                              : 'bg-fsgu-silver-50 border-fsgu-silver-300 text-fsgu-silver-700'
                          }`}
                        />
                        <button
                          type="button"
                          onClick={() => copyToClipboard(contentImages[`image${num}`], num)}
                          className={`px-3 py-1.5 text-xs rounded transition-colors inline-flex items-center ${
                            copiedUrl === num
                              ? 'bg-fsgu-green-600 text-white'
                              : 'bg-fsgu-green-500 hover:bg-fsgu-green-600 text-white'
                          }`}
                        >
                          {copiedUrl === num ? (
                            <>
                              <Check className="w-3 h-3 mr-1" />
                              Copied!
                            </>
                          ) : (
                            <>
                              <Copy className="w-3 h-3 mr-1" />
                              Copy
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPostEditor;
